import { useEffect } from 'react'
import { useParams } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { BreadcrumbSetter } from '@/components/Breadcrumbs'
import { DataCard } from '@/components/card'
import { ActionRedirectItem, ActionsMenu } from '@/components/table/actions'
import { FilterableTable } from '@/components/table/base'
import {
  CheckboxExportSelectedToCsvItem,
  CheckboxMenu,
  CheckboxRenewLicenseForOperatorsItem,
  CheckboxSuspendLicenseForOperatorsItem
} from '@/components/table/checkbox'
import { TitleSetter } from '@/components/Title'
import { STORE_STATUS } from '@/constants/store'
import { ROUTE_PARAM } from '@/router/constants'
import { createBulkHandler } from '@/service/bulkOperationsService'
import { ddeApi } from '@/service/ddeApi'
import { type LicensePropertyOperatorData, useLicensePropertyStore } from '@/stores/licenseProperty'

import {
  CSV_HEADERS,
  formatCsvRow,
  LICENSE_PROPERTY_TABLE_COLUMNS,
  LICENSE_PROPERTY_TABLE_FILTERS
} from './licenseProperty.table'

const tableId = 'license-property-operators-table'

export const LicensePropertyView = () => {
  const licenseProperty = useParams()[ROUTE_PARAM.LICENSE_PROPERTY] || ''

  const { data, isFetching, isLoading } = useLicensePropertyStore(
    useShallow((state) => ({
      data: state.data,
      isLoading: state.status === STORE_STATUS.loading,
      isFetching: state.status === STORE_STATUS.fetching
    }))
  )

  const { fetchData, resetState } = useLicensePropertyStore(
    useShallow((state) => ({
      fetchData: state.fetchData,
      resetState: state.resetState
    }))
  )

  useEffect(() => {
    if (licenseProperty) {
      fetchData({ licenseProperty })
    }
    return resetState
  }, [licenseProperty])

  const handleSuspendLicense = createBulkHandler({
    name: 'Suspend License',
    operation: async (operator: LicensePropertyOperatorData) => {
      await ddeApi.endpoints.operators.suspendOperatorLicense(operator.id, licenseProperty)
    },
    refreshData: () => fetchData({ licenseProperty }),
    getItemDisplayName: (operator) => `${operator.name} (${operator.id})`
  })

  const handleRedirectToOperator = (operator: LicensePropertyOperatorData) =>
    `/operators/${operator.id}/licenses`

  const title = `Licenses - ${licenseProperty}`

  return (
    <>
      <TitleSetter title={title} />
      <BreadcrumbSetter param={ROUTE_PARAM.LICENSE_PROPERTY} label={licenseProperty} />
      <DataCard
        title={title}
        headerPostfixActions={
          <CheckboxMenu tableId={tableId} minimumToOpen={1}>
            <CheckboxSuspendLicenseForOperatorsItem
              onSuspend={handleSuspendLicense}
              licenseProperty={licenseProperty}
            />
            <CheckboxRenewLicenseForOperatorsItem
              licenseProperty={licenseProperty}
              onSuccess={() => fetchData({ licenseProperty })}
            />
            <CheckboxExportSelectedToCsvItem
              csvFileName={`${licenseProperty}_operators`}
              csvHeaders={CSV_HEADERS}
              formatCsvRow={formatCsvRow}
            />
          </CheckboxMenu>
        }
      >
        <FilterableTable
          data={data}
          tableId={tableId}
          loading={isLoading}
          fetching={isFetching}
          filters={LICENSE_PROPERTY_TABLE_FILTERS(data)}
          columns={LICENSE_PROPERTY_TABLE_COLUMNS}
          clientSideFilters
          clientSideSortAndPagination
          withCheckboxes
          rowActions={
            <ActionsMenu>
              <ActionRedirectItem redirectTo={handleRedirectToOperator} text="View Operator" />
            </ActionsMenu>
          }
        />
      </DataCard>
    </>
  )
}
